/**
 * Recording vault. Local-first: recordings live in this browser's IndexedDB and
 * never leave the device unless the player explicitly uploads one (cloud vault,
 * not built yet). Keeps the privacy promise on the sales page true.
 *
 * Recorder composites the camera + skeleton overlay onto one canvas so the
 * saved clip looks like what the player saw.
 */

const DB = 'hooper-vault', STORE = 'clips';
export const FREE_GB = 2; // local quota shown on the meter; the paid tier lives in the cloud vault

function open() {
  return new Promise((res, rej) => {
    const r = indexedDB.open(DB, 1);
    r.onupgradeneeded = () => r.result.createObjectStore(STORE, { keyPath: 'id' });
    r.onsuccess = () => res(r.result);
    r.onerror = () => rej(r.error);
  });
}
const tx = async (mode, fn) => {
  const db = await open();
  return new Promise((res, rej) => {
    const t = db.transaction(STORE, mode);
    const out = fn(t.objectStore(STORE));
    t.oncomplete = () => res(out?.result ?? out);
    t.onerror = () => rej(t.error);
  });
};

export const listClips = () => tx('readonly', (s) => s.getAll()).then((all) => all.sort((a, b) => b.at.localeCompare(a.at)));
export const getClip = (id) => tx('readonly', (s) => s.get(id));
export const deleteClip = (id) => tx('readwrite', (s) => s.delete(id));
export const saveClip = (clip) => tx('readwrite', (s) => s.put(clip));
export async function usage() {
  const clips = await listClips();
  const bytes = clips.reduce((a, c) => a + (c.size || 0), 0);
  return { bytes, gb: bytes / 1e9, count: clips.length, freeGb: FREE_GB, pct: Math.min(100, (bytes / (FREE_GB * 1e9)) * 100) };
}

/**
 * Starts recording video+overlay. Returns { stop() -> Promise<Blob> }.
 * opts.maxSide caps the recording's long side: it is drawn and encoded on the page every frame, and a
 * 4K camera made that a 4K canvas plus a 4K encode beside the tracking (2026-10-04). opts.region()
 * says where the camera picture sits on the overlay canvas (app.js overlayGeom, screen pixels), since
 * the overlay is sized to the screen, not to the camera.
 */
export function startRecording(video, overlay, { fps = 30, maxSide = 1920, region = null } = {}) {
  const canvas = document.createElement('canvas');
  const vw = video.videoWidth || 1280, vh = video.videoHeight || 720;
  const sc = Math.min(1, maxSide / Math.max(vw, vh));
  canvas.width = Math.round(vw * sc); canvas.height = Math.round(vh * sc);
  const ctx = canvas.getContext('2d');
  let on = true;
  (function paint() {
    if (!on) return;
    // A source with no size cannot be drawn: drawImage throws on it. That used
    // to happen BEFORE the next frame was asked for, so one bad frame killed
    // this loop for good and the rest of the recording was a single frozen
    // picture. And it was not rare: the overlay is left at 0 by 0 when a
    // session ends, so the second session of the day hit it on its first paint.
    // Skip what cannot be drawn, never let a frame stop the loop.
    try {
      if (video.videoWidth && video.videoHeight) {
        // Mirror to match the on-screen view.
        ctx.save(); ctx.translate(canvas.width, 0); ctx.scale(-1, 1);
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height); ctx.restore();
      }
      const g = region?.();
      if (overlay.width && overlay.height) {
        if (g) ctx.drawImage(overlay, g.ox * g.k, g.oy * g.k, g.pw * g.k, g.ph * g.k, 0, 0, canvas.width, canvas.height);
        else ctx.drawImage(overlay, 0, 0, canvas.width, canvas.height);
      }
    } catch (e) { /* one frame lost is better than every frame after it */ }
    requestAnimationFrame(paint);
  })();
  let stream, rec;
  const mime = ['video/webm;codecs=vp8', 'video/webm;codecs=vp9', 'video/webm'].find((m) => MediaRecorder.isTypeSupported(m)) || '';
  try {
    stream = canvas.captureStream(fps);
    rec = new MediaRecorder(stream, { mimeType: mime, videoBitsPerSecond: canvas.width * canvas.height > 1e6 ? 2_500_000 : 1_500_000 });
  } catch (e) { on = false; throw e; }   // no recorder here: the drawing loop above must not run on for nothing
  const chunks = [];
  rec.ondataavailable = (e) => { if (e.data.size) chunks.push(e.data); };
  const started = Date.now();
  rec.start(1000);
  // First frame as the thumbnail.
  let poster = null;
  setTimeout(() => { try { poster = canvas.toDataURL('image/jpeg', 0.6); } catch {} }, 1200);
  return {
    stop() {
      return new Promise((res) => {
        rec.onstop = () => {
          on = false; stream.getTracks().forEach((t) => t.stop());
          // The recorder's own type: Safari records mp4 when no webm is on offer, and a blob labelled
          // webm then would not play back there.
          res({ blob: new Blob(chunks, { type: (rec.mimeType || mime || 'video/webm').split(';')[0] }), duration: (Date.now() - started) / 1000, poster });
        };
        rec.state === 'inactive' ? rec.onstop() : rec.stop();
      });
    },
  };
}

export const fmtBytes = (b) => b >= 1e9 ? `${(b / 1e9).toFixed(2)} GB` : b >= 1e6 ? `${(b / 1e6).toFixed(0)} MB` : `${Math.round(b / 1e3)} KB`;
// A webm straight out of MediaRecorder carries no duration, so a <video>
// reports Infinity for it and this used to print "Infinity:NaN" on screen and
// into the companion's prompt. The clip's own recorded length is the fallback.
export const fmtDur = (s) => (Number.isFinite(s) && s >= 0 ? `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, '0')}` : '--:--');

/* ------------------------------------------------------------ cloud vault */
const PART = 8 * 1024 * 1024; // 8 MB parts: under Workers' body limit, over R2's 5 MB minimum

export async function cloudList() {
  const r = await fetch('/api/vault'); if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || 'Cloud Vault unavailable');
  return r.json();
}

/** Multipart upload of a local clip. onProgress(0..1). */
export async function cloudUpload(clip, onProgress) {
  const j = { 'content-type': 'application/json' };
  const start = await fetch(`/api/vault/${clip.id}/start`, { method: 'POST', headers: j, body: JSON.stringify({ size: clip.size, type: clip.blob.type }) });
  if (!start.ok) throw new Error((await start.json().catch(() => ({}))).error || 'Could not start upload');
  const { uploadId } = await start.json();
  const parts = [];
  const total = Math.ceil(clip.blob.size / PART);
  for (let n = 1; n <= total; n++) {
    const chunk = clip.blob.slice((n - 1) * PART, n * PART);
    const r = await fetch(`/api/vault/${clip.id}/part?uploadId=${encodeURIComponent(uploadId)}&n=${n}`, { method: 'PUT', body: chunk });
    if (!r.ok) throw new Error(`Part ${n} failed (${r.status})`);
    parts.push(await r.json());
    onProgress?.(n / total);
  }
  // sessionId: without it a cloud clip played in her room with no shot marks, no calls and no plan.
  const meta = { at: clip.at, duration: clip.duration, attempts: clip.attempts, makes: clip.makes, form: clip.form, companion: clip.companion, poster: clip.poster, sessionId: clip.sessionId ?? null };
  const done = await fetch(`/api/vault/${clip.id}/complete`, { method: 'POST', headers: j, body: JSON.stringify({ uploadId, parts, meta }) });
  if (!done.ok) throw new Error((await done.json().catch(() => ({}))).error || 'Could not finish upload');
  return done.json();
}
export const cloudDelete = (id) => fetch(`/api/vault/${id}`, { method: 'DELETE' }).then((r) => r.json());
export const cloudStreamUrl = (id) => `/api/vault/${id}/stream`;
